import React from "react";
import Modal from "react-modal";
import PropTypes from "prop-types";
import { AiOutlineHome } from "react-icons/ai";
import ChartTypeSelector from "./ChartTypeSelector";
import InfoHelpModal from "./InfoHelpModal";
import studies from "../../Data/studies.json";
import { findStudyByName } from "../../utils/studies";

const GENDER_COMPARISON_NOTE =
  'Note: Gender response categories were updated over time - earlier waves offered "Man", "Woman", and "Other", while later waves added separate options for "Non-binary" and "Other". For consistency across years, these have been combined into a single "Other (e.g., Trans, non-binary, two-spirit, genderqueer)" category. For the exact wording information, please consult the codebooks from 2019 to 2021.';

const NEWS_TIME_SPENT_COMPARISON_NOTE =
  'Note: In 2019, the response categories for "time spent on news per day" were slightly different ("1-10 minutes" and "11-30 minutes" instead of "1-15 minutes" and "16-30 minutes" used from 2020 onward). Values are presented on a consistent scale, but exact time ranges for these categories may not be perfectly comparable to later years. For the exact wording information, please consult the codebook for 2019.';

const getComparisonCategoryNote = (selectedIndependent, selectedIndepMeta) => {
  const selectedId = String(selectedIndependent || "").toLowerCase();
  const selectedText = `${selectedIndepMeta?.label || ""} ${selectedIndepMeta?.question || ""}`
    .trim()
    .toLowerCase();

  if (/gender(id)?\b/.test(selectedId) || selectedText.includes("gender")) {
    return GENDER_COMPARISON_NOTE;
  }

  if (
    selectedId.includes("news_time_spent") ||
    selectedText.includes("news consumption") ||
    selectedText.includes("news each day")
  ) {
    return NEWS_TIME_SPENT_COMPARISON_NOTE;
  }

  return "";
};

const ChartControls = ({
  selectedFile,
  setSelectedFile,
  selectedIndependent,
  setSelectedIndependent,
  indepGroups,
  filters,
  isModalOpen,
  toggleModal,
  selectedFilters,
  handleFilterChange,
  selectedDependent,
  setSelectedDependent,
  depGroups,
  dependentFilters,
  isDepModalOpen,
  toggleDepModal,
  selectedDependentFilters,
  handleDependentFilterChange,
  selectedIndepMeta,
  selectedDepMeta,
  chartType,
  setChartType,
  graphTitle,
  setGraphTitle,
  useWeightedData,
  setUseWeightedData,
  isFormComplete,
  generateGraph,
  graphDataLength,
  exportToPDF,
  isDataLoading,
  dataError,
  isExporting,
  exportError,
  comparisonStudyOptions,
  selectedComparisonStudyNames,
  setSelectedComparisonStudyNames,
  canCompareBetweenYears,
  isComparisonDataLoading,
  comparisonDataError,
  yearComparisonError,
  onCompareBetweenYears,
  isYearComparisonActive,
  selectedYearLabel,
  activeComparisonYearLabels,
  outcomeResponseEntries,
  numericOutcomeGuide,
}) => {
  const [isCompareModalOpen, setIsCompareModalOpen] = React.useState(false);
  const canGenerate = isFormComplete && !isDataLoading && !dataError;
  const canExport = graphDataLength > 0 && !isExporting;
  const canOpenCompareModal = canCompareBetweenYears && comparisonStudyOptions.length > 0;
  const canApplyYearComparison =
    selectedComparisonStudyNames.length > 0 && !isComparisonDataLoading;
  const comparisonCategoryNote = getComparisonCategoryNote(selectedIndependent, selectedIndepMeta);

  const handleToggleComparisonStudy = (studyName) => {
    setSelectedComparisonStudyNames((current) => {
      if (current.includes(studyName)) {
        return current.filter((name) => name !== studyName);
      }
      return [...current, studyName];
    });
  };

  const handleSelectAllComparisonYears = () => {
    setSelectedComparisonStudyNames(comparisonStudyOptions.map((studyOption) => studyOption.name));
  };

  const handleClearComparisonYears = () => {
    setSelectedComparisonStudyNames([]);
  };

  const handleGenerateClick = () => {
    if (!canGenerate) return;
    generateGraph();
  };

  const handleExportClick = () => {
    if (!canExport) return;
    exportToPDF();
  };

  const handleOpenCompareModal = () => {
    if (!canOpenCompareModal) return;
    setIsCompareModalOpen(true);
  };

  const handleCloseCompareModal = () => {
    setIsCompareModalOpen(false);
  };

  const handleApplyYearComparison = () => {
    onCompareBetweenYears();
    setIsCompareModalOpen(false);
  };

  return (
    <section className="w-full">
      <div className="mb-4 relative flex items-center justify-center">
        <h1 className="text-[#181010] text-2xl font-bold text-center">Create a New Chart</h1>

        <a
          href="/"
          className="absolute right-0 top-0 flex flex-col items-center text-gray-700 hover:text-gray-900 focus:outline-none"
          aria-label="Back to Home"
        >
          <AiOutlineHome className="w-7 h-7" />
          <span className="text-[10px] leading-3 mt-1">Back to Home</span>
        </a>
      </div>

      <div className="mb-4">
        <label htmlFor="chart-study" className="flex flex-col">
          <p className="text-[#181010] text-base font-medium mb-2">
            Study <span className="text-red-600">*</span>
          </p>
          <select
            id="chart-study"
            value={selectedFile?.name || ""}
            onChange={(event) => {
              const name = event.target.value;
              setSelectedFile(findStudyByName(studies, name));
            }}
            className="w-full h-14 rounded-xl bg-[#f5f0f0] p-4 text-base"
          >
            <option value="">Select a Study</option>
            {studies.map((category) => (
              <optgroup key={category.key} label={category.category}>
                {category.files.map((file) => (
                  <option key={file.name} value={file.name}>
                    {file.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        {isDataLoading && <p className="text-xs text-gray-500 mt-2">Loading survey data...</p>}
        {dataError && (
          <p className="text-xs text-red-600 mt-2" role="status" aria-live="polite">
            {dataError}
          </p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="chart-dependent" className="flex flex-col">
          <p className="text-[#181010] text-base font-medium mb-2">
            Outcome <span className="text-red-600">*</span>
          </p>
          <select
            id="chart-dependent"
            value={selectedDependent}
            onChange={(event) => setSelectedDependent(event.target.value)}
            className="w-full h-14 rounded-xl bg-[#f5f0f0] p-4 text-base"
          >
            <option value="">What do you want to explore?</option>
            {depGroups.map((group) => (
              <optgroup key={group.group} label={group.group}>
                {group.options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.question}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      </div>
      {selectedDependent && outcomeResponseEntries.length > 0 && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white px-3 py-3">
          <p className="text-sm font-semibold text-[#181010]">Outcome Response Guide</p>
          {numericOutcomeGuide ? (
            <div className="mt-2 space-y-1.5">
              <div className="rounded-md bg-gray-50 px-2.5 py-1.5 text-xs leading-4 text-gray-700">
                {numericOutcomeGuide.scaleLabel}
              </div>
              <div className="rounded-md bg-gray-50 px-2.5 py-1.5 text-xs leading-4 text-gray-700">
                <strong>0:</strong> {numericOutcomeGuide.lowLabel}
              </div>
              <div className="rounded-md bg-gray-50 px-2.5 py-1.5 text-xs leading-4 text-gray-700">
                <strong>100:</strong> {numericOutcomeGuide.highLabel}
              </div>
            </div>
          ) : (
            <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5 pr-1">
              {outcomeResponseEntries.slice(0, 30).map((entry, index) => (
                <div
                  key={`${entry}-${index}`}
                  className="rounded-md bg-gray-50 px-2.5 py-1.5 text-xs leading-4 text-gray-700"
                >
                  {entry}
                </div>
              ))}
              {outcomeResponseEntries.length > 30 && (
                <p className="text-[11px] text-gray-500">
                  +{outcomeResponseEntries.length - 30} more responses
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {dependentFilters.length > 0 && (
        <div className="mb-4">
          <button
            type="button"
            className="w-full h-14 bg-[#f5f0f0] text-[#181010] rounded-xl text-base font-medium"
            onClick={toggleDepModal}
            aria-label="Open outcome filters"
          >
            Outcome Filters
          </button>

          <Modal
            isOpen={isDepModalOpen}
            onRequestClose={toggleDepModal}
            contentLabel="Filter Dependent Selection"
            ariaHideApp={false}
            className="relative z-[60] bg-white p-6 rounded-lg shadow-lg max-w-lg w-[92%] mx-auto my-10 max-h-[80vh] overflow-y-auto"
            overlayClassName="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto px-4"
          >
            <h2 className="text-lg font-bold mb-4">Select Outcome Filters</h2>
            <select
              multiple
              size={Math.min(Math.max(dependentFilters.length, 6), 12)}
              value={selectedDependentFilters}
              onChange={handleDependentFilterChange}
              className="w-full rounded-xl bg-[#f5f0f0] px-4 py-2 text-base overflow-y-auto"
              aria-label="Outcome filters"
            >
              {dependentFilters.map((filter) => (
                <option key={filter.id} value={filter.id}>
                  {filter.display}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-4 mt-4">
              <button
                type="button"
                className="bg-gray-300 px-4 py-2 rounded-lg"
                onClick={toggleDepModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-[#ff0000] text-white px-4 py-2 rounded-lg"
                onClick={toggleDepModal}
              >
                Apply Filters
              </button>
            </div>
          </Modal>
        </div>
      )}

      <div className="mb-4">
        <label htmlFor="chart-independent" className="flex flex-col">
          <p className="text-[#181010] text-base font-medium mb-1">
            Comparison Categories <span className="text-gray-500 font-normal">(Optional)</span>
          </p>
          <p className="text-xs text-gray-500 mb-2">
            Optional: compare outcome by another category.
          </p>
          <select
            id="chart-independent"
            value={selectedIndependent}
            onChange={(event) => setSelectedIndependent(event.target.value)}
            className="w-full h-14 rounded-xl bg-[#f5f0f0] p-4 text-base"
          >
            <option value="">No comparison category</option>
            {indepGroups.map((group) => (
              <optgroup key={group.group} label={group.group}>
                {group.options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.question}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        {comparisonCategoryNote && (
          <div
            className="mt-3 rounded-lg border border-gray-200 bg-white px-3.5 py-3 shadow-sm"
            role="note"
          >
            <p className="mt-1 text-sm leading-5 text-gray-700">{comparisonCategoryNote}</p>
          </div>
        )}
      </div>

      {filters.length > 0 && (
        <div className="mb-4">
          <button
            type="button"
            className="w-full h-14 bg-[#f5f0f0] text-[#181010] rounded-xl text-base font-medium"
            onClick={toggleModal}
            aria-label="Open comparison category filters"
          >
            Comparison Filters
          </button>

          <Modal
            isOpen={isModalOpen}
            onRequestClose={toggleModal}
            contentLabel="Filter Selection"
            ariaHideApp={false}
            className="relative z-[60] bg-white p-6 rounded-lg shadow-lg max-w-lg w-[92%] mx-auto my-10 max-h-[80vh] overflow-y-auto"
            overlayClassName="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto px-4"
          >
            <h2 className="text-lg font-bold mb-4">Select Comparison Filters</h2>
            <div className="mb-4">
              <label className="flex flex-col">
                <select
                  multiple
                  size={Math.min(Math.max(filters.length, 6), 12)}
                  value={selectedFilters}
                  onChange={handleFilterChange}
                  className="w-full rounded-xl bg-[#f5f0f0] px-4 py-2 text-base overflow-y-auto"
                  aria-label="Comparison category filters"
                >
                  {filters.map((filter) => (
                    <option key={filter.id} value={filter.id}>
                      {filter.display}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                className="bg-gray-300 px-4 py-2 rounded-lg"
                onClick={toggleModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-[#ff0000] text-white px-4 py-2 rounded-lg"
                onClick={toggleModal}
              >
                Apply Filters
              </button>
            </div>
          </Modal>
        </div>
      )}

      <div className="mb-4">
        <label htmlFor="chart-title" className="flex flex-col">
          <p className="text-[#181010] text-base font-medium mb-2">
            Graph Title <span className="text-red-600">*</span>
          </p>
          <input
            id="chart-title"
            type="text"
            value={graphTitle}
            onChange={(event) => setGraphTitle(event.target.value)}
            placeholder="Enter graph title"
            className="w-full h-14 rounded-xl bg-[#f5f0f0] p-4 text-base"
          />
        </label>
      </div>

      <div className="mb-4 rounded-xl border border-[#e7dada] bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-1">
              <p className="text-sm font-semibold text-[#181010]">Weighted data</p>
              <InfoHelpModal
                ariaLabel="Show what survey weights are and what they do"
                title="Weighted Data"
                buttonClassName="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#181010]/20"
                iconClassName="h-[15px] w-[15px]"
                modalMaxWidthClassName="max-w-2xl"
              >
                <div>
                  <p className="font-semibold text-[#181010]">What weights are and what they do</p>
                  <p>
                    Survey weights adjust the influence of each respondent so the sample better
                    reflects the target population. They are used when some groups are over, or
                    under-represented in the survey, helping correct these imbalances so results
                    more closely represent the population.
                  </p>
                </div>
                <div>
                  <p>
                    Here you can choose a weighted dataset or an unweighted dataset. When weighted
                    data is enabled, both the graph and statistics use the survey weights.
                  </p>
                </div>
              </InfoHelpModal>
            </div>
            <p className="text-xs text-gray-500">
              Apply survey quota weights to both graphs and statistics.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={useWeightedData}
            onClick={() => setUseWeightedData((value) => !value)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              useWeightedData ? "bg-[#181010]" : "bg-gray-300"
            }`}
            aria-label="Toggle weighted data"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                useWeightedData ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      <div className="mb-4">
        <ChartTypeSelector
          indepType={selectedIndepMeta?.type}
          depType={selectedDepMeta?.type}
          hasComparison={Boolean(selectedIndependent)}
          chartType={chartType}
          setChartType={setChartType}
        />
      </div>

      <div className="flex items-center space-x-4 mt-4">
        {isFormComplete && (
          <button
            type="button"
            className="flex-1 h-14 bg-[#ff0000] text-white rounded-xl text-lg font-bold"
            onClick={handleGenerateClick}
            aria-disabled={!canGenerate}
          >
            Generate Graph
          </button>
        )}

        {graphDataLength > 0 && (
          <button
            type="button"
            className="flex-1 h-14 bg-[#ff0000] text-white rounded-xl text-lg font-bold"
            onClick={handleExportClick}
            aria-disabled={!canExport}
            aria-busy={isExporting}
          >
            Export Chart as PDF
          </button>
        )}
      </div>
      {isFormComplete && (
        <div className="mt-4">
          <button
            type="button"
            className="w-full h-14 bg-[#ff0000] text-white rounded-xl text-lg font-bold"
            onClick={handleOpenCompareModal}
            aria-disabled={!canOpenCompareModal}
          >
            Compare between Years
          </button>
        </div>
      )}

      <Modal
        isOpen={isCompareModalOpen}
        onRequestClose={handleCloseCompareModal}
        contentLabel="Compare between Years"
        ariaHideApp={false}
        className="relative z-[60] bg-white p-6 rounded-lg shadow-lg max-w-lg w-[92%] mx-auto my-10 max-h-[80vh] overflow-y-auto"
        overlayClassName="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto px-4"
      >
        <h2 className="text-lg font-bold mb-2">Compare between Years</h2>
        <p className="text-sm text-gray-600 mb-4">Current year: {selectedYearLabel || "-"}</p>

        <div className="mb-2">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-sm font-medium text-[#181010]">Year(s) to compare</p>
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
              {selectedComparisonStudyNames.length} selected
            </span>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              onClick={handleSelectAllComparisonYears}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={handleClearComparisonYears}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
          <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-[#f8f8f8] p-2">
            {comparisonStudyOptions.map((studyOption) => {
              const isSelected = selectedComparisonStudyNames.includes(studyOption.name);
              return (
                <label
                  key={studyOption.name}
                  className={`mb-2 flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 transition ${
                    isSelected
                      ? "border-[#181010] bg-white shadow-sm"
                      : "border-transparent bg-white/60 hover:bg-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleComparisonStudy(studyOption.name)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#181010] focus:ring-[#181010]/30"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#181010] leading-tight truncate">
                      {studyOption.name}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {isComparisonDataLoading && (
          <p className="text-xs text-gray-500 mt-3" role="status" aria-live="polite">
            Loading data...
          </p>
        )}
        {comparisonDataError && (
          <p className="text-xs text-red-600 mt-3" role="status" aria-live="polite">
            {comparisonDataError}
          </p>
        )}

        <div className="flex justify-end gap-4 mt-5">
          <button
            type="button"
            className="bg-gray-300 px-4 py-2 rounded-lg"
            onClick={handleCloseCompareModal}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-[#ff0000] text-white px-4 py-2 rounded-lg disabled:opacity-50"
            onClick={handleApplyYearComparison}
            disabled={!canApplyYearComparison}
          >
            Compare
          </button>
        </div>
      </Modal>

      {isYearComparisonActive && (
        <p className="text-xs text-gray-500 mt-2" role="status" aria-live="polite">
          Comparing {selectedYearLabel}
          {activeComparisonYearLabels.length
            ? ` with ${activeComparisonYearLabels.join(", ")}`
            : ""}
          .
        </p>
      )}
      {yearComparisonError && (
        <p className="text-xs text-red-600 mt-2" role="status" aria-live="polite">
          {yearComparisonError}
        </p>
      )}
      {exportError && (
        <p className="text-xs text-red-600 mt-2" role="status" aria-live="polite">
          {exportError}
        </p>
      )}
      {isExporting && (
        <p className="text-xs text-gray-500 mt-2" role="status" aria-live="polite">
          Exporting PDF...
        </p>
      )}
    </section>
  );
};

ChartControls.propTypes = {
  selectedFile: PropTypes.shape({ name: PropTypes.string }),
  setSelectedFile: PropTypes.func.isRequired,
  selectedIndependent: PropTypes.string.isRequired,
  setSelectedIndependent: PropTypes.func.isRequired,
  indepGroups: PropTypes.array.isRequired,
  filters: PropTypes.array.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  selectedFilters: PropTypes.array.isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  selectedDependent: PropTypes.string.isRequired,
  setSelectedDependent: PropTypes.func.isRequired,
  depGroups: PropTypes.array.isRequired,
  dependentFilters: PropTypes.array.isRequired,
  isDepModalOpen: PropTypes.bool.isRequired,
  toggleDepModal: PropTypes.func.isRequired,
  selectedDependentFilters: PropTypes.array.isRequired,
  handleDependentFilterChange: PropTypes.func.isRequired,
  selectedIndepMeta: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    question: PropTypes.string,
    type: PropTypes.string,
  }),
  selectedDepMeta: PropTypes.shape({ type: PropTypes.string }),
  chartType: PropTypes.string.isRequired,
  setChartType: PropTypes.func.isRequired,
  graphTitle: PropTypes.string.isRequired,
  setGraphTitle: PropTypes.func.isRequired,
  useWeightedData: PropTypes.bool.isRequired,
  setUseWeightedData: PropTypes.func.isRequired,
  isFormComplete: PropTypes.bool.isRequired,
  generateGraph: PropTypes.func.isRequired,
  graphDataLength: PropTypes.number.isRequired,
  exportToPDF: PropTypes.func.isRequired,
  isDataLoading: PropTypes.bool.isRequired,
  dataError: PropTypes.string,
  isExporting: PropTypes.bool.isRequired,
  exportError: PropTypes.string,
  comparisonStudyOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,
  selectedComparisonStudyNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedComparisonStudyNames: PropTypes.func.isRequired,
  canCompareBetweenYears: PropTypes.bool.isRequired,
  isComparisonDataLoading: PropTypes.bool.isRequired,
  comparisonDataError: PropTypes.string,
  yearComparisonError: PropTypes.string,
  onCompareBetweenYears: PropTypes.func.isRequired,
  isYearComparisonActive: PropTypes.bool.isRequired,
  selectedYearLabel: PropTypes.string,
  activeComparisonYearLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  outcomeResponseEntries: PropTypes.arrayOf(PropTypes.string).isRequired,
  numericOutcomeGuide: PropTypes.shape({
    scaleLabel: PropTypes.string.isRequired,
    lowLabel: PropTypes.string.isRequired,
    highLabel: PropTypes.string.isRequired,
  }),
};

export default React.memo(ChartControls);
